import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PaymentTypeName, ProviderFinancePeriodItem, ProviderFinancePeriodItemType } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { CalendarDateService, formatTime } from '@uplift/services';
import { CollapseList, FlexTable, FlexTableColumn, TableColumn } from '@uplift/ui-v2';
import { useCallback, useMemo, useState } from 'react';
import { AppointmentDetailsCell, AppointmentDetailsInlineCell } from './AppointmentDetailsCell';
import { ClientCell } from './ClientCell';
import { DateTimeCell } from './DateTimeCell';
import { PaidCell } from './PaidCell';
import { getEncounterDate } from '../../helpers';


const useStyles = makeStyles()({
  noPaddingLeftCell: {
    paddingLeft: 0,
  },
  paymentCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    textAlign: 'right',
    paddingRight: '30px',
  }
});

export type FinanceMonthTableProps = {
  rows: ProviderFinancePeriodItem[];
  isLoading?: boolean;
};

const useFinanceMonthTableColumns = () => {
  const timezone = useTimezone();
  const l10n = useL10n();
  const me = useCurrentuser();
  const { classes } = useStyles();

  return useMemo(() => ([
    {
      field: 'submittedAt',
      headerName: l10n.finance.monthTable.submitted,
      renderCell: params => <DateTimeCell {...params} timezone={timezone} />,
      sortable: true,
    } as FlexTableColumn<ProviderFinancePeriodItem, 'submittedAt'>,
    {
      field: 'itemType',
      headerName: l10n.finance.monthTable.encounterDate,
      renderCell: ({ row }) => {
        const encounterDate = getEncounterDate(row);

        return (
          <DateTimeCell
            timezone={timezone}
            value={row.itemType === ProviderFinancePeriodItemType.billableClinicalNote
              ? CalendarDateService.date(encounterDate, DateFormats.ISO_DATE)
              : encounterDate}
            showTime={row.itemType !== ProviderFinancePeriodItemType.billableClinicalNote}
          />
        );
      }
    } as FlexTableColumn<ProviderFinancePeriodItem, 'itemType'>,
    {
      field: 'patient',
      headerName: l10n.finance.monthTable.client,
      renderCell: params => <ClientCell {...params} />
    } as FlexTableColumn<ProviderFinancePeriodItem, 'patient'>,
    {
      field: 'addressState',
      headerName: l10n.finance.monthTable.clientState,
      renderCell: params => {
        return <Typography>{params.value}</Typography>;
      },
    } as FlexTableColumn<ProviderFinancePeriodItem, 'addressState'>,
    {
      field: 'itemType',
      headerName: l10n.finance.monthTable.description,
      cellClassName: classes.noPaddingLeftCell,
      renderCell: params => <AppointmentDetailsCell {...params} />,
    } as FlexTableColumn<ProviderFinancePeriodItem, 'itemType'>,
    {
      field: 'payoutAmount',
      headerName: me.rules?.msoPayout
        ? l10n.finance.monthTable.submittedBillings
        : l10n.finance.monthTable.payment,
      renderCell: params => <PaidCell {...params} hidePaid={me.rules?.msoPayout} />,
      width: 146,
      cellClassName: classes.paymentCell,
    } as FlexTableColumn<ProviderFinancePeriodItem, 'payoutAmount'>,
  ]), [ timezone, l10n.finance.monthTable, me.rules?.msoPayout, classes.noPaddingLeftCell ]);
};

export const FinanceMonthTableDesktop: React.VFC<FinanceMonthTableProps> = ({ rows, isLoading }) => {
  const l10n = useL10n();
  const financeMonthTableColumns = useFinanceMonthTableColumns();

  return (
    <Paper elevation={1}>
      <FlexTable
        isLoading={isLoading}
        rows={rows}
        columns={financeMonthTableColumns}
        pinColumn="start"
        cellHeight={72}
        headCellHeight={56}
        infiniteScroll={{
          perPage: 20
        }}
        emptyOverlayProps={{
          message: l10n.finance.noSessionsForThisPeriod
        }}
      />
    </Paper>
  );
};

const useFinanceMonthTableColumnsMobile = () => {
  const timezone = useTimezone();
  const l10n = useL10n();
  const me = useCurrentuser();

  return useMemo(() => ([
    {
      field: 'submittedAt',
      headerName: l10n.finance.monthTable.submitted,
      sortable: true,
      renderCell: params => {
        if (typeof params.value === 'string') {
          const date = CalendarDateService.date(params.value).tz(timezone);

          return <Typography variant="body2" color="textPrimary">{date.format(DateFormats['MM/DD'])}</Typography>;
        }

        return null;
      },
    } as TableColumn<ProviderFinancePeriodItem, 'submittedAt'>,
    {
      field: 'patient',
      headerName: l10n.finance.monthTable.client,
      renderCell: params => <ClientCell {...params} disableLink />
    } as TableColumn<ProviderFinancePeriodItem, 'patient'>,
    {
      field: 'payoutAmount',
      headerName: me.rules?.msoPayout
        ? l10n.finance.monthTable.billings
        : l10n.finance.monthTable.payment,
      renderCell: params => <PaidCell {...params} hidePaid={me.rules?.msoPayout} />,
    } as TableColumn<ProviderFinancePeriodItem, 'payoutAmount'>,
    {
      field: 'itemType',
      headerName: l10n.finance.monthTable.encounterDate,
      renderCell: ({ row }) => {
        switch (row.itemType) {
          case ProviderFinancePeriodItemType.cancelledSession:
          case ProviderFinancePeriodItemType.completedSession: {
            const date = CalendarDateService.date(row.appointment.fromDatetime).tz(timezone);

            return <Typography variant="body2" color="textPrimary">{date.format(DateFormats['MM/DD/YY'])} at {formatTime(date)}</Typography>;
          }
          case ProviderFinancePeriodItemType.billableClinicalNote: {
            const date = CalendarDateService.date(row.note.submittedAt).tz(timezone);

            return <Typography variant="body2" color="textPrimary">{date.format(DateFormats['MM/DD/YY'])}</Typography>;
          }
        }
      }
    } as TableColumn<ProviderFinancePeriodItem, 'itemType'>,
    {
      field: 'addressState',
      headerName: l10n.finance.monthTable.clientState,
      renderCell: params => {
        return <Typography variant="body2" color="textPrimary">{params.value}</Typography>;
      },
    } as TableColumn<ProviderFinancePeriodItem, 'addressState'>,
    {
      field: 'itemType',
      headerName: l10n.finance.monthTable.description,
      renderCell: params => <AppointmentDetailsInlineCell {...params} />
    } as TableColumn<ProviderFinancePeriodItem, 'itemType'>,
    {
      field: 'payment',
      headerName: l10n.finance.monthTable.payment,
      renderCell: params => {
        const value = params.value?.type === PaymentTypeName.insurance ? l10n.finance.paymentType.insurance : l10n.finance.paymentType.cash;

        return <Typography variant="body2" color="textPrimary">{value}</Typography>;
      }
    } as TableColumn<ProviderFinancePeriodItem, 'payment'>,
  ]), [ l10n.finance.paymentType, timezone, l10n.finance.monthTable, me.rules?.msoPayout ]);
};

export const FinanceMonthTableMobile: React.VFC<FinanceMonthTableProps> = ({ rows }) => {
  const l10n = useL10n();
  const columns = useFinanceMonthTableColumnsMobile();
  const [ loading, setLoading ] = useState(false);
  const [ itemsCount, setItemsCount ] = useState(20);

  const handleInfiniteScroll = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setItemsCount(itemsCount => itemsCount + 20);
    }, 500);
  }, []);

  return (
    <Box mx={-2}>
      <CollapseList
        loading={loading}
        columns={columns}
        rows={rows.slice(0, itemsCount)}
        headerColumnsCount={3}
        disableCollapsibleRowFixedTableLayout

        emptyMessage={l10n.finance.noSessionsForThisPeriod}

        onInfiniteScroll={handleInfiniteScroll}
      />
    </Box>
  );
};

export const FinanceMonthTable: React.VFC<FinanceMonthTableProps> = props => {
  const isMobile = useIsMobile('sm');

  return isMobile ? <FinanceMonthTableMobile {...props} /> : <FinanceMonthTableDesktop {...props} />;
};
